import React, { useRef, useState } from 'react';
import Swal from '../librarianSwal';
import useModalA11y from '../components/useModalA11y';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  addPayment,
  initiateMembershipPayment,
  getErrorMessage
} from '../../api/api';
import './CollectPaymentModal.css';

const MANUAL_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'bank_transfer', label: 'Bank Transfer / POS' }
];

const PAYMENT_MODE_OPTIONS = [
  ...MANUAL_MODES,
  { value: 'hdfc', label: 'HDFC Online (Card / UPI / NetBanking)' }
];

/**
 * Shared "Collect Payment" modal for a membership's pending security
 * deposit. Used from the Members list (an existing pending_payment member),
 * from the Register Member modal's success step, and from the
 * backward-compatible full-page registration route.
 *
 * `amount` is always the server-calculated amount actually due, and is never
 * rendered as an editable field. `requiredAmount` / `paidAmount` are optional
 * display-only figures for the summary card — callers that don't pass them
 * fall back to treating the whole `amount` as freshly due.
 *
 * Manual modes confirm the payment immediately via the existing
 * POST /payments endpoint. HDFC creates a gateway session and redirects the
 * browser to checkout — the browser comes back to
 * /librarian/payments/callback, which re-confirms the real status with the
 * backend before showing success.
 */
const CollectPaymentModal = ({
  memberId,
  membershipId,
  amount,
  memberName,
  membershipNo,
  purpose = 'Membership Security Deposit',
  requiredAmount,
  paidAmount = 0,
  onClose,
  onSuccess,
  onOrderCreated
}) => {
  const [paymentMode, setPaymentMode] = useState('cash');
  const [processing, setProcessing] = useState(false);
  const containerRef = useRef(null);

  const dueAmount = Number(amount || 0);
  const totalRequired = Number(requiredAmount ?? amount ?? 0);
  const alreadyPaid = Number(paidAmount || 0);

  const close = () => {
    if (processing) return;
    onClose();
  };

  useModalA11y({
    isOpen: true,
    onEscape: close,
    containerRef,
    restoreFocusRef: null
  });

  const handleManualPayment = async () => {
    if (processing) return;

    try {
      setProcessing(true);

      await addPayment({
        member_id: memberId,
        membership_id: membershipId,
        payment_for: 'membership_deposit',
        amount: dueAmount,
        payment_method: paymentMode
      });

      Swal.fire('Success', 'Payment recorded and membership activated', 'success');
      onSuccess();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleHdfcPayment = async () => {
    if (processing) return;

    try {
      setProcessing(true);

      const session = await initiateMembershipPayment(membershipId);
      onOrderCreated?.(session.internal_order_id);

      if (session.redirect_url) {
        window.location.href = session.redirect_url;
        return;
      }

      Swal.fire(
        'Payment session created',
        `Order ${session.internal_order_id} was created but no checkout link was returned. Check payment status shortly.`,
        'info'
      );
      onClose();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = () => {
    if (processing) return;
    if (paymentMode === 'hdfc') {
      handleHdfcPayment();
    } else {
      handleManualPayment();
    }
  };

  return (
    <div className="cp-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="cp-modal" role="dialog" aria-modal="true" aria-labelledby="cp-modal-title" ref={containerRef}>
        <div className="cp-modal__header">
          <div className="cp-modal__header-left">
            <i className="fas fa-hand-holding-dollar cp-modal__header-icon" />
            <h2 className="cp-modal__title" id="cp-modal-title">Collect Payment</h2>
          </div>
          <button
            type="button"
            className="cp-modal__close"
            onClick={close}
            disabled={processing}
            aria-label="Close Collect Payment dialog"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="cp-modal__body">
          <div className="cp-summary">
            {memberName && (
              <div className="cp-summary__row">
                <span>Member</span>
                <strong>{memberName}</strong>
              </div>
            )}
            {membershipNo && (
              <div className="cp-summary__row">
                <span>Membership No.</span>
                <strong>{membershipNo}</strong>
              </div>
            )}
            <div className="cp-summary__row">
              <span>Purpose</span>
              <strong>{purpose}</strong>
            </div>
            <div className="cp-summary__row">
              <span>Required Amount</span>
              <strong>₹{totalRequired}</strong>
            </div>
            {alreadyPaid > 0 && (
              <div className="cp-summary__row">
                <span>Already Paid</span>
                <strong>₹{alreadyPaid}</strong>
              </div>
            )}
            <div className="cp-summary__due">
              <span>₹{dueAmount} Due</span>
            </div>
          </div>

          <div className="cp-field">
            <label className="form-label" htmlFor="cp-payment-mode">Payment Mode</label>
            <SearchableSelect
              id="cp-payment-mode"
              name="paymentMode"
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
              options={PAYMENT_MODE_OPTIONS}
              disabled={processing}
              clearable={false}
            />
          </div>

          {paymentMode === 'hdfc' && (
            <div className="cp-hdfc-note">
              <i className="fas fa-shield-halved" />
              <span>You will be redirected to HDFC SmartGATEWAY to complete this payment securely.</span>
            </div>
          )}
        </div>

        <div className="cp-modal__footer">
          <button type="button" className="btn btn-outline-secondary" onClick={close} disabled={processing}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={processing}>
            {processing ? (
              <><span className="spinner-border spinner-border-sm me-2" />Processing...</>
            ) : paymentMode === 'hdfc' ? (
              'Proceed to HDFC Checkout'
            ) : (
              'Confirm Payment'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CollectPaymentModal;
